import React from "react";
import { BlockEntrySection } from "./BlockEntries";
import {
  formatMetricValue,
  formatVsLastWeek,
  formatVsTarget,
  StatusChip,
} from "./metricDisplay";

const COLUMN_LABELS = {
  value: "Value",
  vs_last_week: "vs last week",
  vs_target: "vs target",
  status: "Status",
};

// Rows are admin-picked metrics, each with its own optional flag target.
function MetricTableBlock(props) {
  const table = props.table;

  if (!table) {
    return null;
  }
  const columns = table.columns || ["value", "vs_last_week", "status"];

  return (
    <div className="group w-full bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-100">
        {props.onTitleClick ? (
          <button
            type="button"
            onClick={props.onTitleClick}
            className="flex-1 text-left text-[13px] font-semibold text-slate-900 rounded focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            {table.label || "Metrics"}
          </button>
        ) : (
          <p className="text-[13px] font-semibold text-slate-900">
            {table.label || "Metrics"}
          </p>
        )}
        {props.headerActions}
      </div>
      <div className="overflow-x-auto">
        {/* table-fixed so two stacked tables (Sales Recap / Labor Recap)
            line up: with auto layout each sized its columns to its own
            content, and the longer labels in one table shifted its data
            columns out of step with the other's.

            The metric column is capped at a share rather than left to absorb
            the remainder - a greedy label column pushed every value against
            the right edge on a wide screen while leaving the value column
            itself narrow enough to wrap weather onto three lines. The data
            columns carry no width, so they split the rest evenly and grow
            with the viewport.

            NO min-width on desktop, deliberately. A row of two blocks is
            col-span-6 of a 12-col grid, so each table gets roughly half the
            content width - under the old 720px floor a four-column table
            scrolled sideways and cut the status chip off. Without a floor
            the table is exactly its container and the columns simply share
            what there is. The floor is kept for MOBILE only (sm: is the
            small breakpoint in this codebase's reversed, desktop-first
            config), where a single narrow column really would crush them. */}
        <table className="w-full text-[11px] table-fixed sm:min-w-[440px]">
          <thead>
            <tr>
              <th className="w-[28%] text-left text-[9px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1">
                Metric
              </th>
              {columns.map((column) => (
                <th
                  key={column}
                  className="whitespace-nowrap text-right text-[9px] font-bold uppercase tracking-wider text-slate-400 px-2.5 py-1"
                >
                  {COLUMN_LABELS[column] || column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(table.rows || []).map((row, index) => (
              <tr
                key={index}
                className={
                  index < table.rows.length - 1
                    ? "border-b border-slate-50"
                    : ""
                }
              >
                <td className="px-2.5 py-0.5 font-medium text-slate-900">
                  {row.label}
                </td>
                {columns.map((column) => (
                  <td
                    key={column}
                    className="px-2.5 py-0.5 text-right tabular-nums text-slate-700"
                  >
                    {column === "value" && (
                      <span className="font-semibold text-slate-900">
                        {formatMetricValue(row.value, row.format)}
                      </span>
                    )}
                    {column === "vs_last_week" &&
                      formatVsLastWeek(row.vs_last_week, row.format)}
                    {column === "vs_target" &&
                      formatVsTarget(
                        row.value,
                        row.threshold?.target,
                        row.format
                      )}
                    {column === "status" && (
                      <StatusChip
                        status={row.threshold?.status}
                        showNoTarget={true}
                        compact={true}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {props.canAddEntries !== false && (
        <div className="px-4 pb-2">
          <BlockEntrySection
            blockUuid={props.block.uuid}
            entries={props.entries}
            responseOutstanding={props.responseOutstanding}
            currentUser={props.currentUser}
            post_logbook_block_entry={props.post_logbook_block_entry}
            delete_logbook_block_entry={props.delete_logbook_block_entry}
          />
        </div>
      )}
    </div>
  );
}

export default MetricTableBlock;
